import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/charts/styles.css'
import '@mantine/dates/styles.css'
// @mantine/modals nao exporta CSS proprio — herda de @mantine/core
import './styles/tokens/colors.css'
import './styles/tokens/colors-dark.css'
import './index.css'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
// G14 defense-in-depth: garante que dayjs().utc().toISOString() funciona
// em qualquer componente (CustomerDrawer, etc.) sem import per-file.
dayjs.extend(utc)

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { recarregarUmaVez } from './lazyComRetry'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { QueryClient, QueryCache } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { theme } from './theme'
import App from './App'
import {
  QUERY_CACHE_KEY,
  registerQueryClient,
  shouldDehydrateQuery,
} from '@/lib/queryCache'
import { installConsoleErrorListener } from '@/utils/screenContext'
import { initPushResync } from '@/api/pushResync'
import { ensurePushHealthy } from '@/api/pushSubscriptions'

// TASK-2471: install console.error listener (idempotente) + listeners
// globais para erros window-level e promise rejections nao tratadas.
// Spec: CHAMADOS_VISIBILIDADE_SPEC §9.3.7 + §19.2 G-N4/G-N12.
installConsoleErrorListener()

function generateBootErrorId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 8)
  }
  return Math.random().toString(36).slice(2, 10)
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.error instanceof Error) {
      window.__lastScreenError = {
        error: event.error,
        errorInfo: { componentStack: '(window.error)' },
        errorId: generateBootErrorId(),
      }
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason =
      event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    window.__lastScreenError = {
      error: reason,
      errorInfo: { componentStack: '(unhandledrejection)' },
      errorId: generateBootErrorId(),
    }
  })
}

const queryClient = new QueryClient({
  // TanStack Query v5 removeu defaultOptions.queries.onError — o substituto
  // oficial e QueryCache({ onError }), executado para cada query que falha.
  queryCache: new QueryCache({
    onError: (err: unknown) => {
      if (typeof window !== 'undefined') {
        const error = err instanceof Error ? err : new Error(String(err))
        window.__lastScreenError = {
          error,
          errorInfo: { componentStack: '(react-query onError)' },
          errorId: generateBootErrorId(),
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 1000 * 60 * 60, // 1h — manter cache em memoria por 1 hora
      retry: 1,
    },
  },
})

// Registra a instância para os paths de logout limparem o cache (M-06).
registerQueryClient(queryClient)

// Persistir cache no localStorage — sobrevive a F5 e navegacao
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: QUERY_CACHE_KEY,
  // Limitar tamanho: descartar entradas se localStorage > 4MB
  throttleTime: 1000,
})

// O Vite emite `vite:preloadError` quando o PRELOAD de um chunk falha — caminho
// que não passa pelo `catch` do `lazy()`, porque acontece antes de alguém navegar.
// Mesma causa (deploy trocou `assets/`), mesmo remédio: um reload por aba.
window.addEventListener('vite:preloadError', (e) => {
  recarregarUmaVez((e as unknown as { payload?: unknown }).payload ?? e,
                   () => window.location.reload())
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <ModalsProvider>
        <Notifications position="top-right" />
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: 1000 * 60 * 60, // Cache valido por 1 hora
            // Bumpar quando uma resposta cacheada se tornar incompativel com o
            // bundle novo (ex.: dedup que evita Mantine MultiSelect duplicate
            // options — incidente 2026-05-18 com tenant 113 em N organizacoes).
            // M-06 (TASK-2830): bump invalida os caches PII legados já gravados
            // nos clientes de uma vez.
            buster: 'privacy-allowlist-2026-07-25',
            // M-06: só persiste queries na allowlist NON-PII (queryCache.ts).
            // PII (organizations, org-users, users, chat-*, finops*,
            // permission-templates, …) NUNCA vai para o localStorage.
            dehydrateOptions: { shouldDehydrateQuery },
          }}
        >
          <App />
        </PersistQueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>,
)

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      // Após registrar: liga o re-sync da push subscription (recupera quando o
      // navegador rotaciona a subscription — ver pushResync.ts / sw.js).
      .then(() => {
        initPushResync()
        void ensurePushHealthy()
      })
      .catch(() => { /* best-effort */ })
  })
}
